import { useState } from "react";
import type { ConversationSummary, FolderSummary } from "@/lib/store";

type Props = {
  conv: ConversationSummary;
  active: boolean;
  loading?: boolean;
  streaming?: boolean;
  folders: FolderSummary[];
  indented?: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onMove: (folderId: string | null) => void;
  onTogglePin: (pinned: boolean) => void;
};

function formatDate(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function ChatRow({ conv, active, loading, streaming, folders, indented, onSelect, onDelete, onMove, onTogglePin }: Props) {
  const [showMove, setShowMove] = useState(false);
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-chat-id", conv.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className={`group relative flex items-center gap-2 border-b border-border/50 py-2.5 pr-3 cursor-pointer transition-colors ${
        indented ? "pl-7" : "pl-3"
      } ${active ? "bg-primary/10" : "hover:bg-muted/50"}`}
      onClick={onSelect}
    >
      {loading && (
        <i className="fa-solid fa-spinner-third fa-spin pointer-events-none absolute left-1 top-1/2 -translate-y-1/2 text-[10px] text-primary" />
      )}
      {!loading && streaming && (
        <span className="pointer-events-none absolute left-1 top-1/2 -translate-y-1/2 inline-flex size-1.5 rounded-full bg-orange-500 animate-pulse" title="Streaming…" />
      )}
      {conv.pinned && (
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(false); }}
          className="shrink-0 text-primary hover:text-primary/70"
          title="Unpin"
        >
          <i className="fa-solid fa-thumbtack text-[10px]" />
        </button>
      )}
      <p className="flex-1 min-w-0 truncate text-sm">
        {conv.title}
        {conv.systemPrompt !== null && (
          <i className="fa-solid fa-sparkles ml-1.5 text-[10px] text-primary/70" title="Custom prompt" />
        )}
      </p>
      <span className="shrink-0 text-[11px] text-muted-foreground transition-opacity group-hover:opacity-0">
        {formatDate(conv.updatedAt)}
      </span>
      <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2 opacity-0 transition-opacity pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
        {!conv.pinned && (
          <button
            onClick={(e) => { e.stopPropagation(); onTogglePin(true); }}
            className="text-muted-foreground hover:text-foreground"
            title="Pin"
          >
            <i className="fa-solid fa-thumbtack text-xs" />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setShowMove((s) => !s); }}
          className="text-muted-foreground hover:text-foreground"
          title="Move to folder"
        >
          <i className="fa-solid fa-folder-arrow-up text-xs" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-muted-foreground hover:text-destructive"
          title="Delete"
        >
          <i className="fa-solid fa-trash-can text-xs" />
        </button>
      </div>
      {showMove && (
        <div
          className="absolute right-2 top-full z-10 mt-1 w-48 rounded-md border bg-popover py-1 text-sm shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => { onMove(null); setShowMove(false); }}
            className={`w-full px-3 py-1.5 text-left hover:bg-muted ${conv.folderId == null ? "font-medium text-primary" : ""}`}
          >
            <i className="fa-solid fa-inbox mr-2" />Unfiled
          </button>
          {folders.length > 0 && <div className="my-1 border-t" />}
          {folders.map((f) => (
            <button
              key={f.id}
              onClick={() => { onMove(f.id); setShowMove(false); }}
              className={`w-full px-3 py-1.5 text-left hover:bg-muted truncate ${conv.folderId === f.id ? "font-medium text-primary" : ""}`}
            >
              <i className="fa-solid fa-folder mr-2" />{f.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
